import { Prose } from '../../components/Prose'
import { useState } from 'react'
import { useActivities, useAddActivity, useSchedule } from '../../api/hooks'
import { todayIso, trDate } from '../../lib/format'
import {
  DEFAULT_SETTINGS, planForDate, sportName, type ScheduleSettings,
} from '../../lib/schedule'
import { ACTIVITY_BY_KEY, activityKcal, bmr } from '../../lib/energy'
import { ageFrom, type Sex } from '../../lib/body'
import type { Profile } from '../../lib/metrics'

/**
 * Plan ile kayıt arasındaki köprü.
 *
 * Şablondaki "Salı 20:30 tenis" bir PLAN: öğün saatlerini kaydırıyor,
 * hatırlatıcıyı kuruyor. Enerji hesabı ise sadece aktivite KAYDINA bakıyor.
 * İkisini otomatik birleştirmiyoruz çünkü plan gerçekleşmeyebilir — gitmediğin
 * bir tenisi yakmış saymak hedef kaloriyi bozar ve panele güveni bitirir.
 *
 * Bunun yerine tek dokunuş: planlanan spor için "yaptım" düğmesi, aktivite
 * kaydını hazır süre ve aktiviteyle oluşturuyor. Zaten kaydedilmişse düğme
 * yerine kaç kcal olduğu görünüyor.
 *
 * Dünü de soruyoruz: akşam 20:30 tenisi genelde ertesi gün akla geliyor.
 */
export function PlannedSport({ profile, weightKg }: { profile: Profile; weightKg: number }) {
  const sched = useSchedule()
  const activities = useActivities()
  const add = useAddActivity()

  if (sched.isLoading || activities.isLoading || !sched.data) return null

  const settings: ScheduleSettings = {
    ...DEFAULT_SETTINGS,
    strengthPerWeek: profile.strengthPerWeek ?? DEFAULT_SETTINGS.strengthPerWeek,
    makeupWeekday: profile.makeupWeekday ?? DEFAULT_SETTINGS.makeupWeekday,
    makeupTime: (profile.makeupTime ?? DEFAULT_SETTINGS.makeupTime).slice(0, 5),
    preTennisMinutes: profile.preTennisMinutes ?? DEFAULT_SETTINGS.preTennisMinutes,
    sessionMinutes: profile.sessionMinutes ?? DEFAULT_SETTINGS.sessionMinutes,
    postTennisMinutes: profile.postTennisMinutes ?? DEFAULT_SETTINGS.postTennisMinutes,
  }
  const base = { meal1: profile.meal1, meal2: profile.meal2, kitchenClose: profile.kitchenClose }

  const today = todayIso()
  const logged = activities.data ?? []

  /**
   * Bugün + geriye 7 gün.
   *
   * Önce yalnızca bugün ve dün sorulyordu ve bu yetmiyordu: tatile çıkan ya da
   * birkaç gün paneli açmayan biri döndüğünde kayıtsız günleri hiç göremiyor,
   * o sporlar enerji hesabına hiç girmiyordu. Kaybolan veri, yanlış veriden
   * daha sinsi — hiç fark edilmiyor.
   *
   * 7 gün keyfi değil: bir haftalık şablonun tamamı. Daha geriye gitmek listeyi
   * uzatır ve "şimdi bunu hatırlıyor muyum" sorusunu doğurur.
   */
  const gunler = Array.from({ length: 8 }, (_, i) => shiftIso(today, -i))

  const rows = gunler
    .map((date) => {
      const plan = planForDate(
        date, sched.data!.template, sched.data!.overrides, base,
        sched.data!.strengthDoneDates.length, settings)
      if (plan.kind !== 'sport' || !plan.activityKey) return null
      return {
        date,
        activityKey: plan.activityKey,
        minutes: plan.minutes,
        already: logged.find((a) => a.date === date && a.activityKey === plan.activityKey) ?? null,
      }
    })
    .filter((r): r is NonNullable<typeof r> => r !== null)
    // Geçmiş gün zaten kayıtlıysa hatırlatmaya gerek yok; bugün her hâlükârda
    // görünsün (bugünün kaydı varsa da kaç kcal olduğu okunuyor).
    .filter((r) => r.date === today || !r.already)
    // Uzun bir liste "hangisini yapmıştım" sorusunu doğuruyor; en fazla dört
    // satır gösteriyoruz, gerisi takvimden girilir.
    .slice(0, 4)

  if (rows.length === 0) return null

  const sex: Sex = profile.sex === 'female' ? 'female' : 'male'
  const age = profile.birthDate ? ageFrom(profile.birthDate, today) : profile.age
  const b = bmr(weightKg, profile.heightCm, age, sex)

  return (
    <div className="card">
      <h2>Planlanan spor</h2>
      <Prose className="card-h">
        Program bir <strong>plan</strong>; enerji hesabı ise sadece gerçekten yaptıklarına
        bakıyor. Yaptıysan buradan tek dokunuşla kaydet — aynı şeyi aktivite formuna
        tekrar yazmana gerek yok. Son bir haftada cevaplamadığın günler de burada.
      </Prose>

      {rows.map((r) => (
        <PlannedRow
          key={r.date} row={r} isToday={r.date === today}
          weightKg={weightKg} bmrKcal={b}
          onLog={(minutes, kcal) => add.mutate({
            date: r.date, activityKey: r.activityKey, minutes,
            kcalOverride: kcal,
          })}
          pending={add.isPending}
        />
      ))}

      <Prose className="note">
        Otomatik saymıyoruz çünkü plan gerçekleşmeyebilir — yağmur yağar, iş çıkar.
        Gitmediğin bir sporu yakmış saymak günlük hedefini yükseltir ve fark etmeden
        fazla yersin.
      </Prose>
    </div>
  )
}

/** ISO tarihi n gün kaydır — yerel gün, UTC değil. */
function shiftIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  const p = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

interface Row {
  date: string
  activityKey: string
  minutes: number
  already: { minutes: number; kcalOverride: number | null } | null
}

/**
 * Tek satır: süreyi düzeltebiliyorsun ve saatinden okuduğun kaloriyi girebiliyorsun.
 *
 * Ölçülen değer girilirse MET tahmini tamamen devre dışı kalıyor — saat nabza
 * bakıyor, tablo popülasyon ortalamasına. İkisi çeliştiğinde ölçüm kazanır.
 */
function PlannedRow({ row, isToday, weightKg, bmrKcal, onLog, pending }: {
  row: Row
  isToday: boolean
  weightKg: number
  bmrKcal: number
  onLog: (minutes: number, kcal: number | null) => void
  pending: boolean
}) {
  const [open, setOpen] = useState(false)
  const [minutes, setMinutes] = useState(String(row.minutes))
  const [kcal, setKcal] = useState('')

  const m = Math.max(1, Number(minutes) || row.minutes)
  const measured = kcal.trim() === '' ? null : Math.max(0, Number(kcal) || 0)
  const estimate = activityKcal(
    { date: row.date, activityKey: row.activityKey, minutes: m }, weightKg, bmrKcal)

  const logged = row.already
  const loggedKcal = logged
    ? activityKcal({ date: row.date, activityKey: row.activityKey,
                     minutes: logged.minutes, kcalOverride: logged.kcalOverride },
                   weightKg, bmrKcal)
    : 0

  return (
    <div className="day" style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div className="dt">{sportName(row.activityKey)} · {logged?.minutes ?? m} dk</div>
          <div className="dd">
            {/* Artık bir haftaya kadar geriye bakıyoruz; "Dün" demek yanlış
                olurdu. Tarih her zaman yazılı, gün adı yalnızca bugün için. */}
            {isToday ? 'Bugün' : trDate(row.date)} planlıydı ·{' '}
            {logged
              ? <>{Math.round(loggedKcal)} kcal{logged.kcalOverride != null ? ' (ölçülen)' : ' (tahmin)'}</>
              : <>≈{Math.round(measured ?? estimate)} kcal{measured != null ? ' (ölçülen)' : ' net tahmin'}</>}
            {!ACTIVITY_BY_KEY[row.activityKey] && ' · katalogda yok'}
          </div>
        </div>

        {logged ? (
          <span className="pill pill-good">
            ✓ kaydedildi
          </span>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={{ padding: '6px 11px', fontSize: 12.5 }} onClick={() => setOpen(!open)}>
              {open ? 'Kapat' : 'Düzelt'}
            </button>
            <button className="primary" disabled={pending} onClick={() => onLog(m, measured)}>
              Yaptım
            </button>
          </div>
        )}
      </div>

      {open && !logged && (
        <div className="row" style={{ marginTop: 12 }}>
          <div style={{ flex: '0 1 130px' }}>
            <label className="f">Gerçek süre (dk)</label>
            <input type="number" min={1} max={600} inputMode="numeric"
                   value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </div>
          <div style={{ flex: '0 1 170px' }}>
            <label className="f">Saatten kalori (aktif)</label>
            <input type="number" min={0} max={5000} inputMode="numeric" placeholder="boş = tahmin"
                   value={kcal} onChange={(e) => setKcal(e.target.value)} />
          </div>
          <Prose className="note" style={{ flex: '1 1 100%', marginTop: 0 }}>
            Apple Watch'ta <strong>Aktif Kalori</strong>'yi gir, Toplam'ı değil. Toplam,
            o saati yatarak geçirsen de yakacağın dinlenme metabolizmasını içeriyor —
            bizim hesabımız zaten net olduğu için iki kez sayılır ve günlük harcaman
            ~90 kcal şişer.
          </Prose>
        </div>
      )}
    </div>
  )
}
